// Journal: nach Monat gruppiert, Zeile antippen = Details, offene Trades abrechnen (Spec 4.3)
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EMPTY_FORM, api, buildPayload, ddmm, nowLocal, productLabel, signed, tradeToForm } from "./common";
import { Field, Sheet, TradeFields } from "./TradeForm";
import TradeDetail from "./TradeDetail";

export const useTrades = () => useQuery({ queryKey: ["journal", "all"], queryFn: api.getTrades });

const monthKey = (s: string) => String(s || "").slice(0, 7);
const monthLabel = (k: string) => {
  const [y, m] = k.split("-").map(Number);
  return y ? new Date(y, m - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "ohne Datum";
};

export default function JournalTab() {
  const qc = useQueryClient();
  const { data: trades = [], isLoading, isError } = useTrades();
  const [open, setOpen] = useState<number | null>(null);
  const [form, setForm] = useState<any>(null);          // Neu/Bearbeiten
  const [settle, setSettle] = useState<any>(null);      // Abrechnen
  const [err, setErr] = useState<string | null>(null);
  const done = () => { qc.invalidateQueries({ queryKey: ["journal"] }); };
  const errText = (e: any) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Speichern fehlgeschlagen. Pflichtfelder: Symbol, Einstieg, Eröffnet."; };
  const saveM = useMutation({
    mutationFn: (f: any) => f.id ? api.updateTrade(f.id, buildPayload(f)) : api.addTrade(buildPayload(f)),
    onSuccess: () => { done(); setForm(null); setSettle(null); setErr(null); },
    onError: (e: any) => setErr(errText(e)),
  });
  const delM = useMutation({ mutationFn: api.deleteTrade, onSuccess: () => { done(); setOpen(null); } });

  const groups = useMemo(() => {
    const m = new Map<string, any[]>();
    trades.forEach((t: any) => { const k = monthKey(t.opened_at); m.set(k, [...(m.get(k) || []), t]); });
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [trades]);

  return (
    <>
      <div className="j-list">
        {isLoading && <div className="sc-empty">Lädt …</div>}
        {isError && <div className="sc-empty">Journal konnte nicht geladen werden.</div>}
        {!isLoading && !isError && trades.length === 0 && <div className="sc-empty">Noch keine Trades.</div>}
        {groups.map(([k, list]) => {
          const nOpen = list.filter(t => t.status === "open").length;
          return (
            <section key={k}>
              <div className="j-month"><span>{monthLabel(k)}</span>
                <span>{list.length} Trade{list.length === 1 ? "" : "s"}{nOpen ? ` · ${nOpen} offen` : ""}</span></div>
              {list.map(t => {
                const isOpen = t.status === "open", expanded = open === t.id;
                const pnl = t.pnl_pct;
                return (
                  <div key={t.id} className={`j-item${expanded ? " open" : ""}`}>
                    <div className="j-row" role="button" tabIndex={0} aria-expanded={expanded}
                      onClick={() => setOpen(expanded ? null : t.id)}
                      onKeyDown={e => { if (e.key === "Enter") setOpen(expanded ? null : t.id); }}>
                      <div className="sc-body">
                        <div className="sc-name"><span className="sc-company">{t.name || t.symbol}</span>
                          <span className="j-product">{productLabel(t)}</span></div>
                        <div className="sc-time mono">{ddmm(t.opened_at)} → {isOpen ? "offen" : ddmm(t.closed_at) || "–"}</div>
                      </div>
                      {isOpen && !t.is_sparplan
                        ? <button className="j-settle" onClick={e => { e.stopPropagation(); setErr(null); setSettle({ ...tradeToForm(t), closed_at: nowLocal() }); }}>Abrechnen</button>
                        : <span className={`mono j-pnl ${pnl == null ? "" : pnl >= 0 ? "up" : "down"}`}>{pnl == null ? (isOpen ? "offen" : "–") : signed(pnl)}</span>}
                    </div>
                    {expanded && <TradeDetail t={t}
                      onEdit={() => { setErr(null); setForm(tradeToForm(t)); }}
                      onSettle={() => { setErr(null); setSettle({ ...tradeToForm(t), closed_at: nowLocal() }); }}
                      onDelete={() => { if (window.confirm(`${t.name || t.symbol} löschen?`)) delM.mutate(t.id); }} />}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
      <button className="j-add" onClick={() => { setErr(null); setForm({ ...EMPTY_FORM, opened_at: nowLocal() }); }}>Trade manuell anlegen</button>

      {form && (
        <Sheet title={form.id ? `${form.name || form.symbol} bearbeiten` : "Trade anlegen"} onClose={() => setForm(null)}
          footer={<>{err && <span className="jf-err">{err}</span>}
            <button className="jf-btn" onClick={() => setForm(null)}>Abbrechen</button>
            <button className="jf-btn primary" disabled={saveM.isPending || !form.symbol} onClick={() => saveM.mutate(form)}>{saveM.isPending ? "Speichert …" : "Speichern"}</button></>}>
          <TradeFields form={form} f={(k, v) => setForm((p: any) => ({ ...p, [k]: v }))} />
        </Sheet>
      )}
      {settle && (
        <Sheet title={`${settle.name || settle.symbol} abrechnen`} onClose={() => setSettle(null)}
          footer={<>{err && <span className="jf-err">{err}</span>}
            <button className="jf-btn" onClick={() => setSettle(null)}>Abbrechen</button>
            <button className="jf-btn primary" disabled={saveM.isPending || settle.exit_price === "" || settle.exit_price == null}
              onClick={() => saveM.mutate(settle)}>{saveM.isPending ? "Speichert …" : "Abrechnen"}</button></>}>
          <div className="jf-grid">
            <Field label="Ausstieg"><input type="number" step="any" inputMode="decimal" autoFocus value={settle.exit_price ?? ""}
              onChange={e => setSettle((p: any) => ({ ...p, exit_price: e.target.value }))} /></Field>
            <Field label="Geschlossen"><input type="datetime-local" value={settle.closed_at}
              onChange={e => setSettle((p: any) => ({ ...p, closed_at: e.target.value }))} /></Field>
            <Field label="Gebühren Verkauf (€)"><input type="number" step="any" value={settle.fees_exit ?? ""}
              onChange={e => setSettle((p: any) => ({ ...p, fees_exit: e.target.value }))} /></Field>
            <Field label="Steuer (€)"><input type="number" step="any" value={settle.tax ?? ""}
              onChange={e => setSettle((p: any) => ({ ...p, tax: e.target.value }))} /></Field>
          </div>
        </Sheet>
      )}
    </>
  );
}
